import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { siteInfo } from "../mock";
import { Menu, X, Mail, Phone, ChevronRight } from "lucide-react";
import { Separator } from "./ui/separator";

const navItems = [
  { path: "/", label: "Accueil" },
  { path: "/nous-connaitre", label: "Nous connaître" },
  { path: "/nos-actions", label: "Nos actions" },
  { path: "/notre-actualite", label: "Notre actualité" },
  { path: "/nous-rejoindre", label: "Nous rejoindre" },
];

export const Header = () => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-sky-100 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <img
              src={siteInfo.logo}
              alt={siteInfo.name}
              className="h-14 w-14 object-contain transition-transform duration-300 group-hover:scale-105"
            />
            <div className="hidden sm:block">
              <span className="text-lg font-bold text-slate-800 tracking-tight">
                UNSA
              </span>
              <span className="block text-xs text-sky-600 font-medium -mt-0.5">
                AEGIDE DOMITYS
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`relative px-4 py-2 text-sm font-medium rounded-lg transition-colors duration-200 ${
                    isActive
                      ? "text-sky-600 bg-sky-50"
                      : "text-slate-600 hover:text-sky-600 hover:bg-sky-50/50"
                  }`}
                >
                  {item.label}
                  {isActive && (
                    <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-6 h-0.5 bg-sky-500 rounded-full" />
                  )}
                </Link>
              );
            })}
          </nav>

          {/* CTA Button Desktop */}
          <Link
            to="/nous-rejoindre"
            className="hidden lg:inline-flex items-center gap-2 px-5 py-2.5 bg-sky-500 text-white text-sm font-semibold rounded-lg hover:bg-sky-600 transition-colors duration-200 shadow-sm shadow-sky-200"
          >
            Nous rejoindre
            <ChevronRight className="w-4 h-4" />
          </Link>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 text-slate-600 hover:text-sky-600 rounded-lg hover:bg-sky-50 transition-colors"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="lg:hidden pb-4 animate-in slide-in-from-top-2 duration-200">
            <nav className="flex flex-col gap-1">
              {navItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                      isActive
                        ? "text-sky-600 bg-sky-50"
                        : "text-slate-600 hover:text-sky-600 hover:bg-sky-50/50"
                    }`}
                  >
                    {item.label}
                  </Link>
                );
              })}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export const Footer = () => {
  return (
    <footer className="bg-slate-800 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
          {/* Brand */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <img
                src={siteInfo.logo}
                alt={siteInfo.name}
                className="h-12 w-12 object-contain"
              />
              <div>
                <span className="text-lg font-bold text-white">UNSA</span>
                <span className="block text-xs text-sky-300 font-medium -mt-0.5">
                  AEGIDE DOMITYS
                </span>
              </div>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed">
              Section syndicale UNSA au service des salariés d'Aegide Domitys.
            </p>
          </div>

          {/* Quick Links */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-sky-300 uppercase tracking-wider">
              Navigation
            </h3>
            <nav className="flex flex-col gap-2">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className="text-slate-400 hover:text-white text-sm transition-colors duration-200 flex items-center gap-2"
                >
                  <ChevronRight className="w-3 h-3 text-sky-400" />
                  {item.label}
                </Link>
              ))}
            </nav>
          </div>

          {/* Contact */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-sky-300 uppercase tracking-wider">
              Contact
            </h3>
            <div className="space-y-3">
              <a
                href="mailto:contact@unsa-aegide-domitys.fr"
                className="flex items-center gap-3 text-slate-400 hover:text-white text-sm transition-colors duration-200"
              >
                <Mail className="w-4 h-4 text-sky-400" />
                contact@unsa-aegide-domitys.fr
              </a>
            </div>
          </div>
        </div>

        <Separator className="my-8 bg-slate-700" />

        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-slate-500 text-xs">
            © {new Date().getFullYear()} UNSA AEGIDE DOMITYS. Tous droits réservés.
          </p>
          <p className="text-slate-500 text-xs">
            Syndicat indépendant et responsable
          </p>
        </div>
      </div>
    </footer>
  );
};

const Layout = ({ children }) => {
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
};

export default Layout;
